"use client";

import React from "react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";

export default function QueryParamFilter(props: {
  parameters: { id: string; value: string; flipped?: true }[];
  title?: string;
  field: string;
}): React.ReactNode {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const onChange = React.useCallback(
    (id: string) => {
      const params = new URLSearchParams(searchParams.toString());

      const current = params.getAll(props.field);

      if (!current.includes(id)) {
        params.append(props.field, id);
      } else {
        params.delete(props.field);
        current
          .filter((val) => val !== id)
          .forEach((val) => params.append(props.field, val));
      }

      if (params.size > 0) {
        const queryString = params.toString();
        router.push(pathname + "?" + queryString, { scroll: false });
      } else {
        router.push(pathname, { scroll: false });
      }
    },
    [router, pathname, searchParams, props.field],
  );

  return (
    <div className="flex flex-col gap-2 text-sm">
      {props.title != null && <p>{props.title}</p>}
      <fieldset
        className="flex flex-row flex-wrap gap-2"
        aria-label="Select parameters to filter by"
      >
        {props.parameters.map(({ value, id, flipped }) => {
          const exists = searchParams.getAll(props.field).includes(id);

          return (
            <div
              key={id}
              className="px-2 py-1 border flex gap-2 has-checked:bg-lightGray cursor-pointer rounded-lg bg-background text-foreground text-sm"
              onClick={(event) => {
                event.preventDefault();
                onChange(id);
              }}
            >
              <input
                name={`${props.field}-${id}`}
                id={`${props.field}-${id}`}
                value={value}
                checked={flipped === true ? !exists : exists}
                onChange={(e) => e.preventDefault()}
                type="checkbox"
                className="cursor-pointer"
              />

              <label htmlFor={`parameters-${id}`} className="cursor-pointer">
                {
                  props.parameters.find((parameter) => parameter.id === id)
                    ?.value
                }
              </label>
            </div>
          );
        })}
      </fieldset>
    </div>
  );
}

export function OptionParamFilter(props: {
  parameters: { id: string; value: string }[];
  title?: string;
  field: string;
  default: string;
}): React.ReactNode {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const onChange = React.useCallback(
    (id: string) => {
      const params = new URLSearchParams(searchParams.toString());

      const current = params.get(props.field) ?? props.default;

      if (current !== id) {
        params.set(props.field, id);
        const queryString = params.toString();
        router.push(pathname + "?" + queryString, { scroll: false });
      }
    },
    [router, pathname, searchParams, props.field, props.default],
  );

  return (
    <div className="flex flex-col gap-2 text-sm">
      {props.title != null && <p>{props.title}</p>}
      <fieldset
        className="flex flex-row flex-wrap gap-2"
        aria-label="Select parameters to sort by"
      >
        {props.parameters.map(({ value, id }) => {
          const current = searchParams.get(props.field);
          const selected =
            current === id || (current == null && props.default === id);

          return (
            <div
              key={id}
              className="px-2 py-1 border flex gap-2 has-checked:bg-lightGray cursor-pointer rounded-lg bg-background text-foreground text-sm"
              onClick={(event) => {
                event.preventDefault();
                onChange(id);
              }}
            >
              <input
                name={`${props.field}-${id}`}
                id={`${props.field}-${id}`}
                value={value}
                checked={selected}
                onChange={(e) => e.preventDefault()}
                type="radio"
                className="cursor-pointer"
              />

              <label htmlFor={`parameters-${id}`} className="cursor-pointer">
                {
                  props.parameters.find((parameter) => parameter.id === id)
                    ?.value
                }
              </label>
            </div>
          );
        })}
      </fieldset>
    </div>
  );
}

export function BooleanQueryParamFilter(props: {
  title?: string;
  field: string;
  label: string;
}): React.ReactNode {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const isChecked = searchParams.has(props.field);

  const onChange = React.useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());

    if (params.has(props.field)) {
      params.delete(props.field);
    } else {
      params.set(props.field, "");
    }

    const queryString = params
      .toString()
      .replace(/=(&|$)/g, "$1")
      .replace(/=$/, "");

    if (queryString) {
      router.push(`${pathname}?${queryString}`, { scroll: false });
    } else {
      router.push(pathname, { scroll: false });
    }
  }, [router, pathname, searchParams, props.field]);

  return (
    <div className="flex flex-col gap-2 text-sm">
      {props.title != null && <p>{props.title}</p>}
      <fieldset
        className="flex flex-row flex-wrap gap-2"
        aria-label="Select parameters to fitler by"
      >
        <div
          className="px-2 py-1 border flex gap-2 has-checked:bg-lightGray cursor-pointer rounded-lg bg-background text-foreground text-sm"
          onClick={(event) => {
            event.preventDefault();
            onChange();
          }}
        >
          <input
            name={props.field}
            id={props.field}
            checked={isChecked}
            onChange={(e) => e.preventDefault()}
            type="checkbox"
            className="cursor-pointer"
          />

          <label htmlFor={props.field} className="cursor-pointer">
            {props.label}
          </label>
        </div>
      </fieldset>
    </div>
  );
}
